
import Layout from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { ExternalLink, ChevronLeft } from "lucide-react";

const PrivacyPolicy = () => {
  return (
    <Layout title="Privacy Policy | Freelance With Andy" path="/privacy-policy">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <Link to="/">
          <Button variant="ghost" className="mb-8 hover:bg-brand-green/10">
            <ChevronLeft className="h-4 w-4 mr-2" /> Back to Home
          </Button>
        </Link>

        <Card className="p-8 shadow-md">
          <div className="space-y-8">
            <div>
              <h1 className="text-3xl font-bold mb-4">Privacy Policy</h1>
              <p className="text-gray-500 text-sm">Effective Date: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
              <Separator className="my-4" />
            </div>
            
            <section className="mb-8">
              <p className="mb-4">
                This Privacy Policy describes how your personal information is collected, used, and shared when you visit or make a purchase from freelancewithandy.com, or use any of our services, applications, or tools (collectively, the "Services").
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">1. Personal Information We Collect</h2>
              <p className="mb-4">
                When you visit the site, we automatically collect certain information about your device, including information about your web browser, IP address, time zone, and some of the cookies that are installed on your device. Additionally, as you browse the site, we collect information about the individual web pages that you view, what websites or search terms referred you to the site, and information about how you interact with the site.
              </p>
              <p className="mb-4">
                We refer to this automatically-collected information as "Device Information."
              </p>
              <p className="mb-4">
                We collect Device Information using the following technologies:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>"Cookies" are data files that are placed on your device and often include an anonymous unique identifier. For more information about cookies, and how to disable cookies, visit <a href="http://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer" className="text-brand-green hover:underline">http://www.allaboutcookies.org</a>.</li>
                <li>"Log files" track actions occurring on the site, and collect data including your IP address, browser type, Internet service provider, referring/exit pages, and date/time stamps.</li>
                <li>"Web beacons," "tags," and "pixels" are electronic files used to record information about how you browse the site.</li>
              </ul>
              <p className="mb-4">
                Additionally, when you make a purchase or attempt to make a purchase through the site, we collect certain information from you, including your name, billing address, shipping address, payment information (including credit card numbers), email address, and phone number. We refer to this information as "Order Information."
              </p>
              <p className="mb-4">
                When we talk about "Personal Information" in this Privacy Policy, we are talking both about Device Information and Order Information.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">2. How We Use Your Personal Information</h2>
              <p className="mb-4">
                We use the Order Information that we collect generally to fulfill any orders placed through the site (including processing your payment information, arranging for shipping, and providing you with invoices and/or order confirmations).
              </p>
              <p className="mb-4">
                Additionally, we use this Order Information to:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Communicate with you;</li>
                <li>Screen our orders for potential risk or fraud;</li>
                <li>When in line with the preferences you have shared with us, provide you with information or advertising relating to our products or services.</li>
                <li>Improve and optimize our site (for example, by generating analytics about how our customers browse and interact with the site).</li>
                <li>Assess the success of our marketing and advertising campaigns.</li>
              </ul>
              <p className="mb-4">
                We use the Device Information that we collect to help us screen for potential risk and fraud (in particular, your IP address), and more generally to improve and optimize our site.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">3. Sharing Your Personal Information</h2>
              <p className="mb-4">
                We share your Personal Information with third parties to help us use your Personal Information, as described above. For example, we use Supabase to power our database and user authentication — you can read more about how Supabase uses your personal information here: <a href="https://supabase.io/privacy" target="_blank" rel="noopener noreferrer" className="text-brand-green hover:underline">https://supabase.io/privacy</a>.
              </p>
              <p className="mb-4">
                We also use Google Analytics to help us understand how our customers use the site. You can read more about how Google uses your personal information here: <a href="https://www.google.com/intl/en/policies/privacy/" target="_blank" rel="noopener noreferrer" className="text-brand-green hover:underline">https://www.google.com/intl/en/policies/privacy/</a>.
              </p>
              <p className="mb-4">
                You can also opt out of Google Analytics here: <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-brand-green hover:underline">https://tools.google.com/dlpage/gaoptout</a>.
              </p>
              <p className="mb-4">
                Finally, we may also share your Personal Information to comply with applicable laws and regulations, to respond to a subpoena, search warrant, or other lawful request for information we receive, or to otherwise protect our rights.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">4. Behavioral Advertising and Social Media Integration</h2>
              <p className="mb-4">
                As described above, we use your Personal Information to provide you with targeted advertisements or marketing communications we believe may be of interest to you.
              </p>
              <p className="mb-4">
                Our site may include integrations with social media platforms such as Facebook, Twitter, LinkedIn, and others. These features may collect your IP address, which page you are visiting on our site, and may set cookies to enable the feature to function properly. Your interactions with these platforms are governed by the privacy policy of the company providing them.
              </p>
              <p className="mb-4">
                For more information about how targeted advertising works, you can visit the Network Advertising Initiative's educational page at: <a href="http://www.networkadvertising.org/understanding-online-advertising/how-does-it-work" target="_blank" rel="noopener noreferrer" className="text-brand-green hover:underline">http://www.networkadvertising.org/understanding-online-advertising/how-does-it-work</a>.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">5. Your Rights</h2>
              <p className="mb-4">
                If you are a European resident, you have the right to access personal information we hold about you and to ask that your personal information be corrected, updated, or deleted. If you would like to exercise this right, please contact us through the contact information below.
              </p>
              <p className="mb-4">
                Additionally, if you are a European resident we note that we are processing your information in order to fulfill contracts we might have with you, or otherwise to pursue our legitimate business interests listed above. Additionally, please note that your information will be transferred outside of Europe, including to Canada and the United States.
              </p>
              <p className="mb-4">
                Under the California Consumer Privacy Act (CCPA), California residents have specific rights regarding their personal information. This section describes your CCPA rights and explains how to exercise those rights.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">6. Data Retention</h2>
              <p className="mb-4">
                When you place an order through the site, we will maintain your Order Information for our records unless and until you ask us to delete this information.
              </p>
              <p className="mb-4">
                When you register an account with us, we keep your account information until you request deletion of your account or we determine that the account has been inactive for an extended period.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">7. Minors</h2>
              <p className="mb-4">
                The Services are not intended for individuals under the age of 16. We do not intentionally collect Personal Information from children under 16. If you are under 16, do not use or provide any information on the Services.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">8. Changes to This Privacy Policy</h2>
              <p className="mb-4">
                We may update this privacy policy from time to time in order to reflect, for example, changes to our practices or for other operational, legal or regulatory reasons. When we post changes to this policy, we will revise the "Effective Date" at the top of the document. We will notify you of any material changes by posting notice of the changes on our website or via email. We encourage you to periodically review this page for the latest information on our privacy practices.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">9. Contact Information</h2>
              <p className="mb-4">
                For more information about our privacy practices, if you have questions, or if you would like to make a complaint, please contact us by e‑mail at me@freelancewithandy.com or by mail using the details provided below:
              </p>
              <p className="mb-4 font-medium">
                Freelance With Andy<br />
                [Physical Address]<br />
                [City, Country, Postal Code]<br />
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">10. Related Documents</h2>
              <p className="mb-4">
                This Privacy Policy should be read alongside our <Link to="/terms-of-service" className="text-brand-green hover:underline">Terms of Service</Link>, which provides additional information about how we operate our website.
              </p>
            </section>
            
            <div className="mt-12 flex items-center justify-between border-t border-gray-200 pt-6">
              <p className="text-gray-600 text-sm">
                Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
              <Button asChild variant="outline">
                <a href="https://freelancewithandy.com/privacy-policy" target="_blank" rel="noopener noreferrer">
                  View Live <ExternalLink className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default PrivacyPolicy;
